import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Avatar,
  Chip,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  alpha,
  CircularProgress,
  useTheme,
  useMediaQuery,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ArrowBack,
  MoreVert,
  ThumbUp,
  ThumbDown,
  ChatBubble,
  Share,
  Bookmark,
  Flag,
  Edit,
  Delete,
  People,
  VolumeUp,
  VolumeOff,
  Groups,
  Send,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import toast from 'react-hot-toast';
import { postApi } from '../services/postApi';
import { communityApi } from '../services/communityApi';
import EditPostModal from '../components/community/EditPostModal';
import ReportPostModal from '../components/Admin/ReportPostModal';
import { PostDetailSkeleton, CommentListSkeleton } from '../components/skeletons';

interface PostDetail {
  id: string;
  title: string;
  content: string;
  type: 'text' | 'image' | 'video';
  mediaUrl?: string;
  mediaType?: 'image' | 'video';
  author: {
    id: string;
    username: string;
    profilePicture?: string;
  };
  community?: {
    id: string;
    name: string;
    slug: string;
    profilePicture?: string;
  };
  tags: string[];
  upvotes: number;
  downvotes: number;
  score: number;
  commentsCount: number;
  isPublic: boolean;
  createdAt: string;
  userVote: 'up' | 'down' | null;
  isAuthor?: boolean;
  updatedAt?: string;
}

interface RelatedPost {
  id: string;
  title: string;
  content: string;
  type: 'text' | 'image' | 'video';
  mediaUrl?: string;
  author: {
    id: string;
    username: string;
    profilePicture?: string;
  };
  community?: {
    id: string;
    name: string;
    slug: string;
  };
  upvotes: number;
  downvotes: number;
  commentsCount: number;
  createdAt: string;
  userVote: 'up' | 'down' | null;
  isAuthor?: boolean;
}

interface Comment {
  id: string;
  content: string;
  author: {
    id: string;
    username: string;
    profilePicture?: string;
  };
  createdAt: string;
  upvotesCount: number;
  downvotesCount: number;
  userVote: 'up' | 'down' | null;
}

const PostDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { mode } = useCustomTheme();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const videoRefs = useRef<{[key: string]: HTMLVideoElement | null}>({});

  const [post, setPost] = useState<PostDetail | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<RelatedPost[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [videoMuted, setVideoMuted] = useState<{[key: string]: boolean}>({});
  const [postMenuAnchor, setPostMenuAnchor] = useState<null | HTMLElement>(null);
  const [sortBy, setSortBy] = useState<'new' | 'top'>('new');
  const [reportModalOpen, setReportModalOpen] = useState(false);
  
  // New states for editing
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isDark = mode === 'dark';
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';

  // Function to handle user profile click
  const handleUserProfileClick = (username: string, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation(); // Prevent event bubbling
    }
    if (username) {
      navigate(`/${username}`);
    }
  };

  const glassEffect = {
    background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(20px)',
    border: isDark 
      ? '1px solid rgba(196, 182, 111, 0.2)' 
      : '1px solid rgba(5, 24, 20, 0.1)',
    boxShadow: isDark 
      ? '0 20px 60px rgba(0,0,0,0.7)' 
      : '0 20px 60px rgba(5, 24, 20, 0.1)',
  };

  const gradientButtonStyle = {
    background: isDark 
      ? `linear-gradient(135deg, ${goldColor} 0%, ${alpha(goldColor, 0.8)} 100%)`
      : `linear-gradient(135deg, ${deepGreen} 0%, #0F3B2E 50%, #1D5C4A 100%)`,
    color: isDark ? deepGreen : goldColor,
    borderRadius: '12px',
    fontWeight: 700,
    textTransform: 'none',
    boxShadow: `0 4px 20px ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: `0 8px 30px ${alpha(isDark ? goldColor : deepGreen, 0.3)}`,
      background: isDark 
        ? `linear-gradient(135deg, ${alpha(goldColor, 0.9)} 0%, ${alpha(goldColor, 0.7)} 100%)`
        : 'linear-gradient(135deg, #051814 0%, #0F3B2E 50%, #1D5C4A 100%)',
    }
  };

  // Fetch post details
  // Fix the fetchPost function to handle errors properly
const fetchPost = async () => {
  try {
    setLoading(true);
    let response;
    
    // Use the authenticated endpoint if user is logged in
    if (user) {
      try {
        response = await postApi.getPostWithUserVote(id || '');
      } catch (authError) {
        console.log('Auth endpoint failed, falling back to public endpoint:', authError);
        // Fall back to public endpoint if auth endpoint fails
        response = await postApi.getPost(id || '');
      }
    } else {
      response = await postApi.getPost(id || '');
    }
    
    if (response.success) {
      const postWithVote: PostDetail = {
        id: response.post.id,
        title: response.post.title,
        content: response.post.content || '',
        type: response.post.type,
        mediaUrl: response.post.mediaUrl,
        mediaType: response.post.mediaType,
        author: {
          id: response.post.author?.id || 'unknown',
          username: response.post.author?.username || 'Unknown',
          profilePicture: response.post.author?.profilePicture
        },
        community: response.post.community ? {
          id: response.post.community.id,
          name: response.post.community.name,
          slug: response.post.community.slug,
          profilePicture: response.post.community.profilePicture
        } : undefined,
        tags: response.post.tags || [],
        upvotes: response.post.upvotes || 0,
        downvotes: response.post.downvotes || 0,
        score: response.post.score || 0,
        commentsCount: response.post.commentsCount || 0,
        isPublic: response.post.isPublic !== false,
        createdAt: response.post.createdAt,
        userVote: response.post.userVote || null,
        isAuthor: response.post.isAuthor || false,
        updatedAt: response.post.updatedAt
      };
      
      setPost(postWithVote);
      
      // Fetch related posts and comments in parallel but don't wait for them
      // to complete before setting loading to false
      Promise.allSettled([
        fetchRelatedPosts(postWithVote.community?.id),
        fetchComments()
      ]).then((results) => {
        // Log any errors but don't throw
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            console.error(`Fetch ${index === 0 ? 'related posts' : 'comments'} failed:`, result.reason);
          }
        });
      });
    } else {
      toast.error(response.error || 'Post not found');
      navigate('/community');
    }
  } catch (error) {
    console.error('Failed to fetch post:', error);
    toast.error('Failed to load post');
    navigate('/community');
  } finally {
    setLoading(false);
  }
};

const handleReportClick = () => {
  setPostMenuAnchor(null);
  setReportModalOpen(true);
};

const handleReportSuccess = () => {
  toast.success('Report submitted successfully! Our team will review it shortly.');
  setReportModalOpen(false);
};

  // Fetch related posts - RESTORED ORIGINAL LOGIC
  // Update fetchRelatedPosts to handle errors gracefully
const fetchRelatedPosts = async (communityId?: string) => {
  try {
    setRelatedLoading(true);
    let response;
    
    if (communityId) {
      response = await communityApi.getCommunityPosts(communityId, {
        sortBy: sortBy === 'new' ? 'new' : 'top',
        page: 1,
        limit: 8
      });
    } else {
      response = await postApi.getPosts({
        sortBy: sortBy === 'new' ? 'new' : 'top',
        page: 1,
        limit: 8
      });
    }
    
    if (response.success) {
      const filteredPosts = (response.posts || [])
        .filter(p => p.id !== id)
        .map(p => ({
          id: p.id,
          title: p.title,
          content: p.content || '',
          type: p.type,
          mediaUrl: p.mediaUrl,
          author: {
            id: p.author?.id || 'unknown',
            username: p.author?.username || 'Unknown',
            profilePicture: p.author?.profilePicture
          },
          community: p.community ? {
            id: p.community.id,
            name: p.community.name,
            slug: p.community.slug
          } : undefined,
          upvotes: p.upvotes || 0,
          downvotes: p.downvotes || 0,
          commentsCount: p.commentsCount || 0,
          createdAt: p.createdAt,
          userVote: p.userVote || null,
          isAuthor: p.isAuthor || false
        }));
      setRelatedPosts(filteredPosts.slice(0, 6));
    }
  } catch (error) {
    console.error('Failed to fetch related posts:', error);
    // Don't show error toast for related posts - it's not critical
  } finally {
    setRelatedLoading(false);
  }
};

  const fetchComments = async () => {
    try {
      setCommentsLoading(true);
      const response = await postApi.getPostComments(id || '');
      
      if (response.success) {
        const formattedComments: Comment[] = response.comments.map(comment => {
          const author = comment.author || {
            id: 'unknown',
            username: 'Unknown User',
            profilePicture: undefined
          };
          
          return {
            id: comment.id,
            content: comment.content,
            author: author,
            createdAt: comment.createdAt,
            upvotesCount: comment.upvotesCount || 0,
            downvotesCount: comment.downvotesCount || 0,
            userVote: comment.userVote || null
          };
        });
        
        setComments(formattedComments);
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  // Handle post update
  const handleUpdatePost = async (updatedPost: any) => {
  try {
    // Extract tags - handle both array and string
    const tagsToSend = Array.isArray(updatedPost.tags) 
      ? updatedPost.tags.join(',')
      : updatedPost.tags;
    
    const response = await postApi.updatePost(post!.id, {
      title: updatedPost.title,
      content: updatedPost.content,
      tags: tagsToSend
    });
    
    if (response.success) {
      setPost(prev => prev ? {
        ...prev,
        title: updatedPost.title,
        content: updatedPost.content,
        tags: response.post.tags || updatedPost.tags,
        updatedAt: response.post.updatedAt || new Date().toISOString()
      } : null);
      
      toast.success('Post updated successfully!');
      setEditModalOpen(false);
    } else {
      toast.error(response.error || 'Failed to update post');
    }
  } catch (error: any) {
    console.error('Update post error:', error);
    toast.error(error.message || 'Failed to update post');
  }
};

  // Handle post deletion
  const handleDeletePost = async () => {
    try {
      setIsDeleting(true);
      const response = await postApi.deletePost(post!.id);
      
      if (response.success) {
        toast.success('Post deleted successfully!');
        navigate('/community');
      } else {
        toast.error(response.error || 'Failed to delete post');
      }
    } catch (error: any) {
      console.error('Delete post error:', error);
      toast.error(error.message || 'Failed to delete post');
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  // Video mute toggle
  const toggleVideoMute = (postId: string) => {
    const video = videoRefs.current[postId];
    if (video) {
      video.muted = !video.muted;
      setVideoMuted(prev => ({
        ...prev,
        [postId]: video.muted
      }));
    }
  };

  const handleVideoRef = (postId: string, el: HTMLVideoElement | null) => {
    videoRefs.current[postId] = el;
    if (el) {
      el.loop = true;
      el.autoplay = true;
      el.playsInline = true;
      el.muted = videoMuted[postId] || true;
    }
  };

  // Post vote handler
  const handleVote = async (postId: string, vote: 'up' | 'down' | 'remove') => {
    if (!user) {
      toast.error('Please login to vote');
      return;
    }

    try {
      let voteToSend = vote;
      if (post && post.id === postId) {
        if ((vote === 'up' && post.userVote === 'up') || 
            (vote === 'down' && post.userVote === 'down')) {
          voteToSend = 'remove';
        }
      }

      const response = await postApi.votePost(postId, voteToSend);
      
      if (response.success) {
        if (post && post.id === postId) {
          setPost({
            ...post,
            upvotes: response.post.upvotes,
            downvotes: response.post.downvotes,
            score: response.post.score,
            userVote: response.post.userVote
          });
        }
        
        setRelatedPosts(prev => prev.map(relatedPost => {
          if (relatedPost.id === postId) {
            return {
              ...relatedPost,
              upvotes: response.post.upvotes,
              downvotes: response.post.downvotes,
              userVote: response.post.userVote
            };
          }
          return relatedPost;
        }));
        
        toast.success(response.message || 'Vote updated');
      }
    } catch (error) {
      console.error('Vote error:', error);
      toast.error('Failed to vote on post');
      if (post && post.id === postId) {
        fetchPost();
      }
    }
  };

  // Add comment handler
  const handleAddComment = async () => {
    if (!user) {
      toast.error('Please login to comment');
      return;
    }

    if (!newComment.trim()) {
      toast.error('Comment cannot be empty');
      return;
    }

    try {
      const response = await postApi.createComment(post!.id, {
        content: newComment
      });

      if (response.success) {
        const newCommentObj: Comment = {
          id: response.comment.id,
          content: response.comment.content,
          author: response.comment.author,
          createdAt: response.comment.createdAt,
          upvotesCount: response.comment.upvotesCount,
          downvotesCount: response.comment.downvotesCount,
          userVote: null
        };

        setComments([newCommentObj, ...comments]);
        setNewComment('');
        
        if (post) {
          setPost({
            ...post,
            commentsCount: post.commentsCount + 1
          });
        }
        
        toast.success('Comment added!');
      } else {
        toast.error('Failed to add comment');
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    }
  };

  // Post menu handlers
  const handlePostMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setPostMenuAnchor(event.currentTarget);
  };

  const handlePostMenuClose = () => {
    setPostMenuAnchor(null);
  };

  const handleEditClick = () => {
    setPostMenuAnchor(null);
    setEditModalOpen(true);
  };

  const handleDeleteClick = () => {
    setPostMenuAnchor(null);
    setDeleteDialogOpen(true);
  };

  useEffect(() => {
    if (id) {
      fetchPost();
    }
  }, [id]);

  useEffect(() => {
    if (post) {
      fetchRelatedPosts(post.community?.id);
    }
  }, [sortBy]);

  if (loading) {
    return <PostDetailSkeleton />;
  }

  if (!post) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: isDark ? '#000000' : '#FAFAFA',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Box sx={{ textAlign: 'center', ...glassEffect, p: 6, borderRadius: '20px' }}>
          <Typography variant="h5" sx={{ mb: 2, color: isDark ? '#fff' : deepGreen }}>
            Post not found
          </Typography>
          <Button 
            onClick={() => navigate('/community')} 
            variant="contained"
            sx={{
              ...gradientButtonStyle,
              px: 4,
              py: 1.5
            }}
          >
            Back to Community
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: isDark ? '#000000' : '#FAFAFA',
      '& *::-webkit-scrollbar': { display: 'none' },
      '& *': { msOverflowStyle: 'none', scrollbarWidth: 'none' }
    }}>
      {/* Back Button - Fixed Position */}
<IconButton
  onClick={() => navigate(-1)}
  sx={{
    position: 'fixed',
    top: { xs: 70, md: 100 },
    left: { xs: 20, md: 140 },
    color: goldColor,
    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(10px)',
    border: `1px solid ${alpha(goldColor, 0.3)}`,
    '&:hover': {
      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)',
      border: `1px solid ${goldColor}`,
      transform: 'scale(1.05)'
    },
    transition: 'all 0.3s ease',
    width: { xs: 35, md: 56 },
    height: { xs: 35, md: 56 },
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
  }}
>
  <ArrowBack sx={{
    height: { xs: 20, md: 28 },
    width: { xs: 20, md: 28 }
  }} />
</IconButton>
      
      {/* Main Content - Restored original spacing and sizes */}
      <Container 
        maxWidth="xl" 
        sx={{ 
          pt: { xs: 8, md: 5 },
          pb: 6,
          px: { xs: 2, sm: 3, md: 4 }
        }}
      >
        <Grid container spacing={4} justifyContent="center">
          {/* Main Post Column - Original width */}
          <Grid size={{ xs: 12, md: 7, lg: 7 }}>
            <Box sx={{ 
              minWidth: { md: '700px' },
              maxWidth: '750px', 
              mx: 'auto' 
            }}>
              {/* Post Card */}
              <Card sx={{ 
                borderRadius: '20px', 
                ...glassEffect,
                overflow: 'hidden',
                mb: 4
              }}>
                <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                  {/* Post Header */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: { xs: 2, md: 3 }, 
                    mb: { xs: 3, md: 4 } 
                  }}>
                    <Avatar
  src={post.author?.profilePicture}
  onClick={(e) => handleUserProfileClick(post.author?.username || '', e)}
  sx={{
    width: { xs: 48, md: 60 },
    height: { xs: 48, md: 60 },
    border: `2px solid ${alpha(goldColor, 0.3)}`,
    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
    cursor: 'pointer', 
    transition: 'transform 0.2s ease', 
    '&:hover': {
      transform: 'scale(1.1)', 
    }
  }}
>
  {post.author?.username?.charAt(0)?.toUpperCase() || 'U'}
</Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: { xs: 1, md: 1.5 }, 
                        flexWrap: 'wrap',
                        mb: 0.5
                      }}>
                        <Typography 
  variant="subtitle1" 
  onClick={(e) => handleUserProfileClick(post.author?.username || '', e)}
  sx={{ 
    fontWeight: 800, 
    color: isDark ? '#fff' : deepGreen, 
    fontSize: { xs: '1rem', md: '1.1rem' },
    cursor: 'pointer', 
    transition: 'color 0.2s ease', 
    '&:hover': {
      color: goldColor, 
    }
  }}
>
  {post.author?.username || 'Unknown'}
</Typography>
                        {post.community && (
                          <>
                            <Typography variant="caption" sx={{ 
                              color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', 
                              fontSize: { xs: '0.7rem', md: '0.8rem' } 
                            }}>
                              in
                            </Typography>
                            <Box
                              component="span"
                              onClick={() => navigate(`/community/${post.community?.slug}`)}
                              sx={{
                                color: goldColor,
                                fontWeight: 800,
                                fontSize: { xs: '0.75rem', md: '0.9rem' },
                                cursor: 'pointer',
                                background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)',
                                px: { xs: 1, md: 1.5 },
                                py: { xs: 0.25, md: 0.5 },
                                borderRadius: '10px',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  background: isDark ? 'rgba(196, 182, 111, 0.2)' : 'rgba(196, 182, 111, 0.1)',
                                  textDecoration: 'none'
                                }
                              }}
                            >
                              wfh/{post.community?.slug}
                            </Box>
                          </>
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ 
                        color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                        fontSize: { xs: '0.7rem', md: '0.8rem' }
                      }}>
                        {new Date(post.createdAt).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                        {post.updatedAt && post.updatedAt !== post.createdAt && (
                          <span style={{ marginLeft: '8px', fontStyle: 'italic' }}>
                            (edited)
                          </span>
                        )}
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={handlePostMenuOpen}
                      sx={{ 
                        color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                        '&:hover': { 
                          bgcolor: alpha(goldColor, 0.1)
                        } 
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>

                  {/* Post Title */}
                  <Typography variant="h4" sx={{
                    fontWeight: 900,
                    mb: { xs: 3, md: 4 },
                    lineHeight: 1.3,
                    color: isDark ? '#fff' : deepGreen,
                    fontSize: { xs: '1.5rem', md: '2.2rem' },
                    wordBreak: 'break-word'
                  }}>
                    {post.title}
                  </Typography>

                  {/* Media Section */}
                  {(post.type === 'image' || post.type === 'video') && post.mediaUrl && (
                    <Box sx={{
                      mb: { xs: 3, md: 4 },
                      borderRadius: '16px',
                      overflow: 'hidden',
                      position: 'relative',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                      maxHeight: { xs: '400px', md: '600px' }
                    }}>
                      {post.type === 'image' ? (
                        <CardMedia
                          component="img"
                          image={post.mediaUrl}
                          alt="Post"
                          sx={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: { xs: '400px', md: '600px' },
                            objectFit: 'contain',
                            backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)'
                          }}
                        />
                      ) : (
                        <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
                          <video
                            ref={(el) => handleVideoRef(post.id, el)}
                            src={post.mediaUrl}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '16px'
                            }}
                            loop
                            autoPlay
                            playsInline
                            muted={videoMuted[post.id] !== false}
                          />
                          <IconButton
                            onClick={() => toggleVideoMute(post.id)}
                            sx={{
                              position: 'absolute',
                              bottom: { xs: 10, md: 16 },
                              right: { xs: 10, md: 16 },
                              bgcolor: 'rgba(0,0,0,0.6)',
                              color: '#fff',
                              width: { xs: 32, md: 40 },
                              height: { xs: 32, md: 40 },
                              '&:hover': {
                                bgcolor: 'rgba(0,0,0,0.8)'
                              }
                            }}
                          >
                            {videoMuted[post.id] === false ? <VolumeUp /> : <VolumeOff />}
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Post Content */}
                  {post.content && (
                    <Typography variant="body1" sx={{
                      mb: { xs: 3, md: 4 },
                      color: isDark ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.95)',
                      lineHeight: 1.8,
                      fontSize: { xs: '1rem', md: '1.2rem' },
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}>
                      {post.content}
                    </Typography>
                  )}

                  {/* Tags Section */}
                  {post.tags && post.tags.length > 0 && (
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: { xs: 0.5, md: 1 }, 
                      mb: { xs: 3, md: 4 } 
                    }}>
                      {post.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={`#${tag}`}
                          size="small"
                          sx={{
                            bgcolor: alpha(goldColor, 0.1),
                            color: goldColor,
                            fontWeight: 600,
                            borderRadius: '10px',
                            fontSize: { xs: '0.75rem', md: '0.9rem' },
                            height: { xs: 28, md: 36 },
                            '&:hover': {
                              bgcolor: alpha(goldColor, 0.2)
                            }
                          }}
                        />
                      ))}
                    </Box>
                  )}

                  {/* Action Buttons */}
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pt: { xs: 2, md: 3 },
                    borderTop: `1px solid ${alpha(goldColor, 0.1)}`,
                    flexWrap: 'wrap',
                    gap: 2
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: { xs: 1, md: 2 },
                      flexWrap: 'wrap'
                    }}>
                      <Button
                        startIcon={<ThumbUp />}
                        onClick={() => handleVote(post.id, post.userVote === 'up' ? 'remove' : 'up')}
                        sx={{
                          minWidth: 'auto',
                          color: post.userVote === 'up' ? goldColor : (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
                          fontWeight: 700,
                          borderRadius: '20px',
                          px: { xs: 1.5, md: 2.5 },
                          py: 0.5,
                          bgcolor: post.userVote === 'up' ? alpha(goldColor, 0.1) : 'transparent',
                          ...(post.userVote === 'up' ? {} : glassEffect),
                          fontSize: { xs: '0.85rem', md: '1rem' },
                          '&:hover': {
                            bgcolor: post.userVote === 'up' ? alpha(goldColor, 0.2) : alpha(goldColor, 0.05)
                          }
                        }}
                      >
                        {post.upvotes}
                      </Button>

                      <Button
                        startIcon={<ThumbDown />}
                        onClick={() => handleVote(post.id, post.userVote === 'down' ? 'remove' : 'down')}
                        sx={{
                          minWidth: 'auto',
                          color: post.userVote === 'down' ? goldColor : (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
                          fontWeight: 700,
                          borderRadius: '20px',
                          px: { xs: 1.5, md: 2.5 },
                          py: 0.5,
                          bgcolor: post.userVote === 'down' ? alpha(goldColor, 0.1) : 'transparent',
                          ...(post.userVote === 'down' ? {} : glassEffect),
                          fontSize: { xs: '0.85rem', md: '1rem' },
                          '&:hover': {
                            bgcolor: post.userVote === 'down' ? alpha(goldColor, 0.2) : alpha(goldColor, 0.05)
                          }
                        }}
                      >
                        {post.downvotes}
                      </Button>

                      <Button
                        startIcon={<ChatBubble />}
                        onClick={() => {
                          document.getElementById('comments-section')?.scrollIntoView({ behavior: 'smooth' });
                        }}
                        sx={{
                          minWidth: 'auto',
                          color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                          fontWeight: 700,
                          borderRadius: '20px',
                          px: { xs: 1.5, md: 2.5 },
                          py: 0.5,
                          ...glassEffect,
                          fontSize: { xs: '0.85rem', md: '1rem' },
                          '&:hover': {
                            bgcolor: alpha(goldColor, 0.05)
                          }
                        }}
                      >
                        {post.commentsCount}
                      </Button>

                      <Button
                        startIcon={<Share />}
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/post/${post.id}`);
                          toast.success('Link copied to clipboard!');
                        }}
                        sx={{
                          minWidth: 'auto',
                          color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                          fontWeight: 600,
                          borderRadius: '20px',
                          px: { xs: 1.5, md: 2.5 },
                          py: 0.5,
                          fontSize: { xs: '0.85rem', md: '1rem' },
                          ...glassEffect,
                          '&:hover': {
                            bgcolor: alpha(goldColor, 0.05)
                          }
                        }}
                      >
                        Share
                      </Button>
                    </Box>
                  </Box>

                  {/* Comments Section */}
                  <Box id="comments-section" sx={{ mt: 4 }}>
                    <Typography variant="h5" sx={{ 
                      mb: 3, 
                      fontWeight: 800, 
                      color: isDark ? '#fff' : deepGreen 
                    }}>
                      Comments ({post.commentsCount})
                    </Typography>

                    {/* Add Comment */}
                    {user && (
                      <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
                        <Avatar
  src={user.profilePicture}
  onClick={(e) => handleUserProfileClick(user.username || '', e)}
  sx={{
    width: 40,
    height: 40,
    border: `2px solid ${alpha(goldColor, 0.3)}`,
    cursor: 'pointer', 
    transition: 'transform 0.2s ease', 
    '&:hover': {
      transform: 'scale(1.1)', 
    }
  }}
>
  {user.username?.charAt(0).toUpperCase()}
</Avatar>
                        <Box sx={{ flex: 1 }}>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            placeholder="Add a comment..."
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                bgcolor: isDark ? alpha('#fff', 0.05) : alpha(deepGreen, 0.03),
                                '& fieldset': {
                                  borderColor: isDark ? alpha(goldColor, 0.3) : alpha(deepGreen, 0.2)
                                },
                                '&:hover fieldset': {
                                  borderColor: goldColor
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: goldColor
                                }
                              },
                              '& .MuiInputBase-input': { 
                                color: isDark ? '#fff' : deepGreen,
                              }
                            }}
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                            <Button
                              startIcon={<Send />}
                              onClick={handleAddComment}
                              disabled={!newComment.trim()}
                              sx={{
                                color: mode === 'dark' ? goldColor : '#051814',
                                background: mode === 'dark'
                                  ? `linear-gradient(135deg, ${deepGreen} 0%, #0F3B2E 50%, #1D5C4A 100%)`
                                  : `linear-gradient(135deg, ${goldColor} 0%, ${alpha(goldColor, 0.8)} 100%)`,
                                border: '2px',
                                borderRadius: '12px',
                                fontWeight: 700,
                                px: 3,
                                py: 0.8,
                                fontSize: '0.9rem',
                                '&:disabled': {
                                  opacity: 0.5
                                }
                              }}
                            >
                              Comment
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {/* Comments List */}
                    {commentsLoading ? (
                      <CommentListSkeleton />
                    ) : comments.length === 0 ? (
                      <Box sx={{ 
                        p: 4, 
                        textAlign: 'center',
                        border: `2px dashed ${alpha(goldColor, 0.3)}`,
                        borderRadius: '12px'
                      }}>
                        <ChatBubble sx={{ fontSize: 40, color: goldColor, mb: 2, opacity: 0.5 }} />
                        <Typography sx={{ 
                          color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                          mb: 2,
                          fontWeight: 600
                        }}>
                          No Comments
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'
                        }}>
                          Be the first to comment on this post!
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {comments.map((comment) => (
                          <Box
                            key={comment.id}
                            sx={{
                              p: 3,
                              borderRadius: '12px',
                              bgcolor: isDark ? alpha('#fff', 0.03) : alpha(deepGreen, 0.02),
                              border: `1px solid ${alpha(goldColor, 0.1)}`
                            }}
                          >
                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                              <Avatar
  src={comment.author.profilePicture}
  onClick={(e) => handleUserProfileClick(comment.author.username || '', e)}
  sx={{
    width: 36,
    height: 36,
    border: `1px solid ${alpha(goldColor, 0.3)}`,
    cursor: 'pointer', 
    transition: 'transform 0.2s ease', 
    '&:hover': {
      transform: 'scale(1.1)', 
    }
  }}
>
  {comment.author.username?.charAt(0).toUpperCase()}
</Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography 
  variant="subtitle2" 
  onClick={(e) => handleUserProfileClick(comment.author.username || '', e)}
  sx={{ 
    fontWeight: 700, 
    color: isDark ? '#fff' : deepGreen,
    cursor: 'pointer', 
    transition: 'color 0.2s ease', 
    '&:hover': {
      color: goldColor, 
    }
  }}
>
  {comment.author.username}
</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'
                                }}>
                                  {new Date(comment.createdAt).toLocaleDateString()}
                                </Typography>
                              </Box>
                            </Box>
                            <Typography variant="body2" sx={{ 
                              mb: 2,
                              color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
                              lineHeight: 1.6
                            }}>
                              {comment.content}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Grid>

          {/* Related Posts Sidebar - Original width */}
          {!isMobile && (
            <Grid size={{ md: 5, lg: 5 }}>
              <Box sx={{ position: 'sticky', top: 20 }}>
                {/* Related Posts Header */}
                <Card sx={{ 
                  borderRadius: '20px', 
                  ...glassEffect,
                  overflow: 'hidden',
                  mb: 3,
                  minWidth: '450px'
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      mb: 3
                    }}>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 800, 
                        color: isDark ? '#fff' : deepGreen,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontSize: '1.1rem'
                      }}>
                        <Groups sx={{ color: goldColor }} />
                        {post.community ? 'More from this community' : 'Trending Posts'}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Chip
                          label="New"
                          size="small"
                          onClick={() => setSortBy('new')}
                          sx={{
                            height: 30,
                            borderRadius: '20px',
                            bgcolor: sortBy === 'new' 
                              ? goldColor 
                              : isDark ? alpha('#fff', 0.05) : alpha(deepGreen, 0.05),
                            color: sortBy === 'new' ? deepGreen : 'inherit',
                            border: `1px solid ${alpha(goldColor, 0.3)}`,
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            '&:hover': {
                              bgcolor: sortBy === 'new' 
                                ? alpha(goldColor, 0.9) 
                                : isDark ? alpha('#fff', 0.1) : alpha(deepGreen, 0.1),
                              borderColor: goldColor
                            }
                          }}
                        />
                        <Chip
                          label="Top"
                          size="small"
                          onClick={() => setSortBy('top')}
                          sx={{
                            height: 30,
                            borderRadius: '20px',
                            bgcolor: sortBy === 'top' 
                              ? goldColor 
                              : isDark ? alpha('#fff', 0.05) : alpha(deepGreen, 0.05),
                            color: sortBy === 'top' ? deepGreen : 'inherit',
                            border: `1px solid ${alpha(goldColor, 0.3)}`,
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            '&:hover': {
                              bgcolor: sortBy === 'top' 
                                ? alpha(goldColor, 0.9) 
                                : isDark ? alpha('#fff', 0.1) : alpha(deepGreen, 0.1),
                              borderColor: goldColor
                            }
                          }}
                        />
                      </Box>
                    </Box>

                    {relatedLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3, minHeight: '300px' }}>
                        <CircularProgress size={24} sx={{ color: goldColor }} />
                      </Box>
                    ) : relatedPosts.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Groups sx={{ fontSize: 40, color: goldColor, mb: 2, opacity: 0.5 }} />
                        <Typography variant="body2" sx={{ 
                          color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' 
                        }}>
                          No related posts found
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        {relatedPosts.map((relatedPost) => (
                          <Box
                            key={relatedPost.id}
                            onClick={() => navigate(`/post/${relatedPost.id}`)}
                            sx={{
                              cursor: 'pointer',
                              borderRadius: '12px',
                              overflow: 'hidden',
                              position: 'relative',
                              transition: 'all 0.2s',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 8px 30px ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
                              }
                            }}
                          >
                            <Box sx={{ 
                              p: 2.5, 
                              borderRadius: '12px',
                              bgcolor: isDark ? alpha('#fff', 0.03) : alpha(deepGreen, 0.02),
                              border: `1px solid ${alpha(goldColor, 0.1)}`
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                                <Avatar
  src={relatedPost.author?.profilePicture}
  onClick={(e) => handleUserProfileClick(relatedPost.author?.username || '', e)}
  sx={{
    width: 36,
    height: 36,
    border: `1px solid ${alpha(goldColor, 0.3)}`,
    cursor: 'pointer', 
    transition: 'transform 0.2s ease', 
    '&:hover': {
      transform: 'scale(1.1)', 
    }
  }}
>
  {relatedPost.author?.username?.charAt(0)?.toUpperCase() || 'U'}
</Avatar>
                                <Box sx={{ flex: 1 }}>
                                  <Typography 
  variant="caption" 
  onClick={(e) => handleUserProfileClick(relatedPost.author?.username || '', e)}
  sx={{ 
    fontWeight: 700,
    color: isDark ? '#fff' : deepGreen,
    display: 'block',
    lineHeight: 1.2,
    fontSize: '0.85rem',
    cursor: 'pointer',
    transition: 'color 0.2s ease', 
    '&:hover': {
      color: goldColor, 
    }
  }}
>
  {relatedPost.author?.username || 'Unknown'}
</Typography>
                                  {relatedPost.community && (
                                    <Typography variant="caption" sx={{ 
                                      color: goldColor,
                                      fontWeight: 600,
                                      fontSize: '0.75rem'
                                    }}>
                                      wfh/{relatedPost.community.slug}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              
                              <Typography variant="body1" sx={{ 
                                fontWeight: 700,
                                color: isDark ? '#fff' : deepGreen,
                                lineHeight: 1.3,
                                mb: 1.5,
                                fontSize: '0.95rem',
                              }}>
                                {relatedPost.title.length > 80
                                  ? `${relatedPost.title.substring(0, 80)}...`
                                  : relatedPost.title}
                              </Typography>
                              
                              {/* Show media if exists */}
                              {relatedPost.mediaUrl && (relatedPost.type === 'image' || relatedPost.type === 'video') && (
                                <Box sx={{ 
                                  mb: 1.5,
                                  borderRadius: '8px',
                                  overflow: 'hidden',
                                  position: 'relative',
                                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                                  height: '220px'
                                }}>
                                  {relatedPost.type === 'image' ? (
                                    <CardMedia
                                      component="img"
                                      image={relatedPost.mediaUrl}
                                      alt="Post"
                                      sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                      }}
                                    />
                                  ) : (
                                    <Box sx={{ position: 'relative', height: '100%' }}>
                                      <video
                                        ref={(el) => handleVideoRef(relatedPost.id, el)}
                                        src={relatedPost.mediaUrl}
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                        }}
                                        loop
                                        autoPlay
                                        playsInline
                                        muted={true}
                                      />
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          bottom: 5,
                                          right: 5,
                                          bgcolor: 'rgba(0,0,0,0.6)',
                                          color: '#fff',
                                          borderRadius: '50%',
                                          width: 24,
                                          height: 24,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: '0.8rem'
                                        }}
                                      >
                                        ▶
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              )}
                              
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                mt: 1.5,
                                pt: 1.5,
                                borderTop: `1px solid ${alpha(goldColor, 0.05)}`
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <ThumbUp sx={{ fontSize: 16, color: goldColor }} />
                                    <Typography variant="caption" sx={{ 
                                      color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                                      fontWeight: 600,
                                      fontSize: '0.8rem'
                                    }}>
                                      {relatedPost.upvotes}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <ChatBubble sx={{ fontSize: 16, color: goldColor }} />
                                    <Typography variant="caption" sx={{ 
                                      color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                                      fontWeight: 600,
                                      fontSize: '0.8rem'
                                    }}>
                                      {relatedPost.commentsCount}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Typography variant="caption" sx={{ 
                                  color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                  fontSize: '0.75rem'
                                }}>
                                  {new Date(relatedPost.createdAt).toLocaleDateString(undefined, {
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {/* Community Info Card */}
                {post.community && (
                  <Card sx={{ 
                    borderRadius: '20px', 
                    ...glassEffect,
                    overflow: 'hidden'
                  }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ 
                        mb: 2, 
                        fontWeight: 800, 
                        color: isDark ? '#fff' : deepGreen,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontSize: '1.1rem'
                      }}>
                        <People sx={{ color: goldColor }} />
                        About Community
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar
                          src={post.community.profilePicture}
                          sx={{
                            width: 56,
                            height: 56,
                            bgcolor: goldColor,
                            color: deepGreen,
                            fontWeight: 700,
                            fontSize: '1.2rem',
                            border: `2px solid ${alpha(goldColor, 0.3)}`
                          }}
                        >
                          {post.community.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" sx={{ 
                            fontWeight: 800, 
                            color: isDark ? '#fff' : deepGreen,
                            fontSize: '1.1rem'
                          }}>
                            wfh/{post.community.slug}
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}>
                            <People sx={{ fontSize: 14 }} />
                            Public Community
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => navigate(`/community/${post.community?.slug}`)}
                        sx={{
                          ...gradientButtonStyle,
                          py: 1.2,
                          fontSize: '0.95rem',
                          fontWeight: 700
                        }}
                      >
                        Visit Community
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Container>

      {/* Post Menu */}
      <Menu
        anchorEl={postMenuAnchor}
        open={Boolean(postMenuAnchor)}
        onClose={handlePostMenuClose}
        PaperProps={{
          sx: {
            ...glassEffect,
            borderRadius: '12px',
            mt: 1
          }
        }}
      >
        {post?.isAuthor && (
          <>
            <MenuItem onClick={handleEditClick}>
              <ListItemIcon>
                <Edit fontSize="small" sx={{ color: goldColor }} />
              </ListItemIcon>
              <ListItemText sx={{ color: isDark ? '#fff' : deepGreen }}>Edit Post</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDeleteClick} sx={{ color: '#ff4444' }}>
              <ListItemIcon>
                <Delete fontSize="small" sx={{ color: '#ff4444' }} />
              </ListItemIcon>
              <ListItemText>Delete Post</ListItemText>
            </MenuItem>
            <Divider sx={{ borderColor: alpha(goldColor, 0.1) }} />
          </>
        )}
        <MenuItem onClick={handlePostMenuClose}>
          <ListItemIcon>
            <Bookmark fontSize="small" sx={{ color: goldColor }} />
          </ListItemIcon>
          <ListItemText sx={{ color: isDark ? '#fff' : deepGreen }}>Save Post</ListItemText>
        </MenuItem>
        <MenuItem onClick={handlePostMenuClose}>
          <ListItemIcon>
            <Share fontSize="small" sx={{ color: goldColor }} />
          </ListItemIcon>
          <ListItemText sx={{ color: isDark ? '#fff' : deepGreen }}>Share Post</ListItemText>
        </MenuItem>
        <Divider sx={{ borderColor: alpha(goldColor, 0.1) }} />
       <MenuItem onClick={handleReportClick} sx={{ color: '#ff4444' }}>
  <ListItemIcon>
    <Flag fontSize="small" sx={{ color: '#ff4444' }} />
  </ListItemIcon>
  <ListItemText>Report Post</ListItemText>
</MenuItem>
      </Menu>

      {/* Edit Post Modal */}
      {post && (
        <EditPostModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSuccess={handleUpdatePost}
          post={{
            id: post.id,
            title: post.title,
            content: post.content,
            tags: post.tags,
            type: post.type,
            community: post.community,
            mediaUrl: post.mediaUrl
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            ...glassEffect,
            borderRadius: '16px',
            p: 2
          }
        }}
      >
        <DialogTitle sx={{ color: isDark ? '#fff' : deepGreen, fontWeight: 700 }}>
          Delete Post
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>
            Are you sure you want to delete this post? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
              fontWeight: 600
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeletePost}
            disabled={isDeleting}
            sx={{
              color: '#ff4444',
              fontWeight: 700,
              '&:hover': {
                backgroundColor: 'rgba(255, 68, 68, 0.1)'
              }
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      {post && (
  <ReportPostModal
    open={reportModalOpen}
    onClose={() => setReportModalOpen(false)}
    onSuccess={handleReportSuccess}
    post={{
      id: post.id,
      title: post.title,
      content: post.content,
      author: {
        id: post.author.id,
        username: post.author.username,
        profilePicture: post.author.profilePicture
      },
      createdAt: post.createdAt,
      community: post.community ? {
        id: post.community.id,
        name: post.community.name,
        slug: post.community.slug
      } : undefined
    }}
  />
)}
    </Box>
  );
};

export default PostDetailPage;